'use client';

import { Box, HStack, token } from '@terra/ui-styled-system';
import { Text } from '../../../../components/styled/text';
import { Tooltip } from '../../../../components/tooltip';
import { useState } from 'react';
import {
  BrowserIcon,
  Tick01Icon,
  HelpCircleIcon,
  Key01Icon,
  Link02Icon,
  Target03Icon,
} from '@terra/ui-icons';
import {
  useEntityResolver,
  isMongoId,
  detectEntityType,
  type EntityType,
  type ResolvedCredential,
  type ResolvedEndpoint,
  type ResolvedSignal,
  type ResolvedApplication,
} from '../context/EntityResolverContext';

type EntityBadgeProps = {
  /** The entity type */
  type: EntityType;
  /** The entity ID to resolve */
  id: string;
  /** Optional: show full ID on hover */
  showIdOnHover?: boolean;
};
const entityIcons: Record<EntityType, typeof Key01Icon> = {
  credential: Key01Icon,
  endpoint: Link02Icon,
  signal: Target03Icon,
  application: BrowserIcon,
};
const entityColors: Record<EntityType, string> = {
  credential: token('colors.fg.neutral.warning'),
  endpoint: token('colors.fg.entity.entryPoint'),
  signal: token('colors.fg.entity.signal'),
  application: token('colors.fg.entity.assets'),
};

function formatCredential(entity: ResolvedCredential): string {
  if (entity.username) {
    return `${entity.name} (${entity.username})`;
  }

  return entity.name;
}

function formatEndpoint(entity: ResolvedEndpoint): string {
  return `${entity.method} ${entity.path}`;
}

function formatSignal(entity: ResolvedSignal): string {
  const maxLength = 40;

  if (entity.description.length > maxLength) {
    return `${entity.description.slice(0, maxLength)}...`;
  }

  return entity.description;
}

function formatApplication(entity: ResolvedApplication): string {
  return entity.name;
}

export function EntityBadge({
  type,
  id,
  showIdOnHover = true,
}: EntityBadgeProps) {
  const { resolve } = useEntityResolver();
  const entity = resolve(type, id);
  const [isCopied, setIsCopied] = useState(false);
  const Icon = entityIcons[type] || HelpCircleIcon;
  const color = entityColors[type] || token('colors.fg.muted');
  // Format display text based on entity type
  let displayText: string;
  let tooltipText: string;

  if (entity) {
    switch (type) {
      case 'credential':
        displayText = formatCredential(entity as ResolvedCredential);
        tooltipText = `Credential: ${displayText}${showIdOnHover ? `\nType: ${(entity as ResolvedCredential).type}\nClick to copy ID` : ''}`;
        break;
      case 'endpoint':
        displayText = formatEndpoint(entity as ResolvedEndpoint);
        tooltipText = `Endpoint: ${(entity as ResolvedEndpoint).host || ''}${(entity as ResolvedEndpoint).path}${showIdOnHover ? '\nClick to copy ID' : ''}`;
        break;
      case 'signal':
        displayText = formatSignal(entity as ResolvedSignal);
        tooltipText = `${(entity as ResolvedSignal).description}${showIdOnHover ? '\nClick to copy ID' : ''}`;
        break;
      case 'application':
        displayText = formatApplication(entity as ResolvedApplication);
        tooltipText = `Application: ${displayText}${showIdOnHover ? '\nClick to copy ID' : ''}`;
        break;
      default:
        displayText = id;
        tooltipText = `${id}${showIdOnHover ? '\nClick to copy' : ''}`;
    }
  } else {
    // Entity not resolved - show truncated ID
    displayText = `${id.slice(0, 8)}...`;
    tooltipText = `${type}: ${id}\nClick to copy`;
  }

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(id);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy ID:', error);
    }
  };

  const badge = (
    <HStack
      gap={1}
      alignItems="center"
      py={0.5}
      px={1.5}
      borderRadius="sm"
      bg="bg.subtle"
      display="inline-flex"
      maxW="200px"
      cursor="pointer"
      onClick={handleClick}
      _hover={{
        bg: 'bg.muted',
        transform: 'scale(1.02)',
      }}
      transition="all 0.2s"
    >
      <Box display="flex" flexShrink={0}>
        <Icon size={12} color={color} />
      </Box>
      <Text
        fontSize="xs"
        fontFamily="mono"
        color="fg.default"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {displayText}
      </Text>
      {isCopied && (
        <Box display="flex" flexShrink={0} ml={1}>
          <Tick01Icon size={10} color={token('colors.colorPalette.9')} />
        </Box>
      )}
    </HStack>
  );

  if (showIdOnHover) {
    return (
      <Tooltip title={tooltipText} subtitle={`ID: ${id}`}>
        {badge}
      </Tooltip>
    );
  }

  return badge;
}

/**
 * Auto-detecting entity badge that determines type from field name
 */
type AutoEntityBadgeProps = {
  /** The field name (e.g., "credentialId", "endpointId") */
  fieldName: string;
  /** The value (should be a MongoDB ObjectId) */
  value: unknown;
};

export function AutoEntityBadge({ fieldName, value }: AutoEntityBadgeProps) {
  // Only render if value looks like a MongoDB ID
  if (!isMongoId(value)) {
    return null;
  }

  // Detect entity type from field name
  const entityType = detectEntityType(fieldName);

  if (!entityType) {
    return null;
  }

  return <EntityBadge type={entityType} id={value} />;
}

/**
 * Renders a value, auto-detecting and replacing entity IDs with badges
 */
type SmartValueProps = {
  fieldName: string;
  value: unknown;
  /** Fallback renderer for non-entity values */
  fallback?: (value: unknown) => React.ReactNode;
};

export function SmartValue({ fieldName, value, fallback }: SmartValueProps) {
  // Check if this is an entity ID
  if (isMongoId(value)) {
    const entityType = detectEntityType(fieldName);

    if (entityType) {
      return <EntityBadge type={entityType} id={value} />;
    }
  }

  // Use fallback or default string representation
  if (fallback) {
    return <>{fallback(value)}</>;
  }

  if (typeof value === 'string') {
    return (
      <Text fontSize="xs" fontFamily="mono">
        {value}
      </Text>
    );
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return (
      <Text fontSize="xs" fontFamily="mono">
        {String(value)}
      </Text>
    );
  }

  return null;
}
